/**
 * Sole owner of the "is this section navigation or footer?" predicate.
 *
 * Before this module existed, the inline literal
 *   `category === 'navigation' || category === 'footer'`
 * appeared in 8+ files across the renderer, composer, exporter, parsers, and
 * property panel. This module centralizes the predicate so future changes
 * (e.g. additional shared categories) only need to touch one place.
 *
 * Scope: predicate centralization only. State is still shaped as
 * `state.sharedSections.{navigation, footer}` — adding a third shared type
 * would still require state-shape changes plus migrations. Generalizing fully
 * is a separate, larger refactor.
 *
 * Behavior contract: exact string comparison, no trimming. Some call sites
 * previously did `String(category).trim()` before compare; others did not.
 * Matching the un-trimmed literal preserves byte-identical export output for
 * both. If a caller needs trim semantics, do it explicitly at the callsite,
 * e.g. `isSharedCategory(category.trim())`.
 *
 * Lines deliberately NOT routed through this module (they are role-specific
 * dispatch, not "is this shared" predicates):
 *   - section-manager.js: footer-only / nav-only branches that do different work per role
 *   - build-template-sections.js: navigation-specific JS-strip path
 *   - property-panel.js: navigation→nav-link-editor, footer→shared-section-info dispatch
 *   - variant-swap.js: getSelectors() returns different selector lists per category
 *   - template-parser.js: HTML tag dispatch (`tag === 'nav'` / `'footer'`) — different concept
 *   - section-list.js: navigation-only variant-popover branching with broader text matching
 *   - add-section-modal.js: navigation-only grid layout mode
 */

export const SHARED_SECTION_CATEGORIES = Object.freeze(['navigation', 'footer']);

/**
 * Conventional ids for shared sections whose source element carries none.
 *
 * Lives here rather than in build-template-sections.js because THREE producers
 * now have to agree on it — the section builder, the thumbnail generator, and
 * the C3 section classifier — and a classifier that skipped id-less sections
 * would leave a template's nav and footer unclassified while every other
 * section got a canonical identity. A `<header>`/`<footer>` with no id is not
 * skippable; that omission has already cost 22 templates their nav and 16
 * their footer once.
 */
export const SHARED_ROLE_FALLBACK_IDS = Object.freeze({ navigation: 'nav', footer: 'footer' });

/**
 * Give an id-less shared section its conventional id — the ONE copy of the
 * loop that build-template-sections.js, classify-sections.js and
 * ingest-fidelity-check.mjs previously each carried inline. Behavior is
 * byte-identical to those loops: per page, never shadowing an id a real
 * section on the SAME page already claims.
 *
 * Mutates the records in place (they carry a non-enumerable `element`
 * reference that DOM CSS attribution depends on — replacing the records would
 * sever it).
 *
 * @param {Array<{id: string|null, role?: string}>} pageSections one page's records
 */
export function synthesizeSharedRoleIds(pageSections) {
  for (const section of pageSections) {
    if (section.id) continue;
    const fallbackId = SHARED_ROLE_FALLBACK_IDS[section.role];
    if (fallbackId && !pageSections.some((s) => s.id === fallbackId)) {
      section.id = fallbackId;
    }
  }
}

/** Source ids invented for id-less CONTENT sections carry this prefix. */
export const SYNTH_CONTENT_ID_PREFIX = 'pb-auto-';

/**
 * How much real content a section record carries: element descendants plus
 * visible text length. Zero means an empty shell. Used only to decide an id
 * collision — here, when two elements on one page share an explicit id, and
 * in build-template-sections.js when an empty shell precedes the real one.
 */
export function sectionContentWeight(section) {
  const html = typeof section?.html === 'string' ? section.html : '';
  if (!html) return 0;
  const tags = (html.match(/<[a-z][^>]*>/gi) || []).length;
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return Math.max(0, tags - 1) + text.length;
}

// Class tokens that describe layout scaffolding or animation state, never the
// section's identity. Skipped when choosing the slug token.
const SYNTH_SKIP_CLASS_TOKENS = new Set(['e-con', 'e-con-inner', 'animated', 'in-view']);
const SYNTH_SKIP_CLASS_RE = /^(slide|fade)-/;

function slugifySynthToken(raw) {
  const slug = String(raw ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)
    .replace(/-+$/, '');
  return slug || null;
}

/**
 * The slug half of a synthesized content-section id: the first class token
 * that names the section (layout/animation utilities skipped), falling back
 * to the tag name, falling back to 'section'. Deterministic; reads the
 * record's live `element` when present, else the serialized html.
 */
export function synthContentSlug(section) {
  let classAttr = '';
  const el = section?.element;
  if (el && typeof el.getAttribute === 'function') {
    classAttr = el.getAttribute('class') || '';
  } else if (typeof section?.html === 'string') {
    const m = section.html.match(/^<[a-z0-9-]+[^>]*\sclass="([^"]*)"/i);
    classAttr = m ? m[1] : '';
  }
  for (const token of classAttr.split(/\s+/)) {
    if (!token || SYNTH_SKIP_CLASS_TOKENS.has(token) || SYNTH_SKIP_CLASS_RE.test(token)) continue;
    const slug = slugifySynthToken(token);
    if (slug) return slug;
  }
  return slugifySynthToken(section?.tagName) || 'section';
}

/**
 * Synthesize stable source ids for id-less CONTENT sections (IF-1a).
 *
 * Contract (each clause pinned by shared-section-synthesis-tests.js):
 *   - Key shape: `pb-auto-{slug}-{hash12}` + `-{n}` from the second identical
 *     sibling on the SAME page (n = 2, 3, …).
 *   - Identical content on different pages SHARES an identity; repeated
 *     identical content on one page gets an ordinal, consistently per page.
 *   - Explicit ids always win: allocation only starts after every explicit id
 *     across the ENTIRE site is collected, and a candidate colliding with any
 *     taken id probes deterministically (`-2` loop).
 *   - EXCEPT a within-page repeat. An explicit id claimed twice on ONE page by
 *     two content-bearing elements is two sections wearing one name (invalid
 *     HTML that Webflow ships: fourhorsemenmedia.com's homepage carries
 *     `<section id="services">` for "What we offer" AND for "Content Creator
 *     Program"). The first keeps the id; every later one is demoted to id-less
 *     and named here, and `duplicateOfId` records what it was demoted from.
 *     Left alone, the builder dropped the second from the base set while
 *     variant detection keyed on (page, id) mapped BOTH page slots to the
 *     second's markup — the homepage shipped the 3-card block twice and the
 *     4-card block nowhere. An EMPTY first occurrence is not demoted past: that
 *     is the a82website shape (an empty `<div id=…>` before the real footer),
 *     which the builder resolves by replacing the shell. Same id on DIFFERENT
 *     pages is untouched — that is a variant, and legitimate.
 *   - Content edits create new identities (the hash is content-derived).
 *   - Page order must be deterministic (homepage first, then the caller's
 *     stable page order); document order within a page.
 *
 * MUST run after synthesizeSharedRoleIds on every page and BEFORE any id
 * filtering, CSS splitting, or DOM-index construction.
 *
 * @param {Array<{pageName: string, sections: Array}>} pages site pages in order
 * @param {{hashHtml: (html: string) => string}} opts sync hex hasher (≥12 chars)
 * @returns {{synthesized: Array<{pageName: string, id: string}>,
 *            demoted: Array<{pageName: string, id: string, duplicateOfId: string}>}}
 */
export function synthesizeContentSectionIds(pages, { hashHtml }) {
  if (typeof hashHtml !== 'function') {
    throw new Error('synthesizeContentSectionIds: hashHtml function is required');
  }
  const demoted = [];
  const demotedEntry = new Map();
  for (const page of pages) {
    const firstById = new Map();
    for (const section of page.sections) {
      if (!section.id) continue;
      const incumbent = firstById.get(section.id);
      if (!incumbent) {
        firstById.set(section.id, section);
        continue;
      }
      if (sectionContentWeight(incumbent) === 0 || sectionContentWeight(section) === 0) continue;
      section.duplicateOfId = section.id;
      section.id = null;
      const entry = { pageName: page.pageName, id: null, duplicateOfId: section.duplicateOfId };
      demoted.push(entry);
      demotedEntry.set(section, entry);
    }
  }

  const takenIds = new Set();
  for (const page of pages) {
    for (const section of page.sections) {
      if (section.id) takenIds.add(section.id);
    }
  }

  const claim = (candidate) => {
    let key = candidate;
    for (let n = 2; takenIds.has(key); n += 1) key = `${candidate}-${n}`;
    takenIds.add(key);
    return key;
  };

  // Memo keyed by (content hash, same-page occurrence) so identical content
  // resolves to identical ids across pages — including ordinal repeats — and
  // an ordinal candidate that collided with an explicit id is remembered under
  // its probed name rather than silently reusing the explicit id later.
  const keyByOccurrence = new Map();
  const synthesized = [];
  for (const page of pages) {
    const perPageHashCount = new Map();
    for (const section of page.sections) {
      if (section.id) continue;
      const html = section.html || '';
      const hash = String(hashHtml(html));
      const occurrence = (perPageHashCount.get(hash) || 0) + 1;
      perPageHashCount.set(hash, occurrence);

      const memoKey = `${hash}:${occurrence}`;
      let id = keyByOccurrence.get(memoKey);
      if (!id) {
        const base =
          occurrence === 1
            ? `${SYNTH_CONTENT_ID_PREFIX}${synthContentSlug(section)}-${hash.slice(0, 12)}`
            : `${keyByOccurrence.get(`${hash}:1`)}-${occurrence}`;
        id = claim(base);
        keyByOccurrence.set(memoKey, id);
      }
      section.id = id;
      section.synthesizedContentId = true;
      synthesized.push({ pageName: page.pageName, id });
      const entry = demotedEntry.get(section);
      if (entry) entry.id = id;
    }
  }
  return { synthesized, demoted };
}

/**
 * @param {*} category
 * @returns {boolean} true iff `category` is exactly 'navigation' or 'footer'.
 */
export function isSharedCategory(category) {
  return category === 'navigation' || category === 'footer';
}

/**
 * @param {*} section
 * @returns {boolean} true iff section?.category is shared.
 */
export function isSharedSection(section) {
  return isSharedCategory(section?.category);
}

/**
 * Some call sites carry `role` (template-parser output) instead of `category`.
 * @param {*} role
 * @returns {boolean}
 */
export function isSharedRole(role) {
  return role === 'navigation' || role === 'footer';
}

/**
 * The state.sharedSections key for a given category, or null.
 * @param {*} category
 * @returns {'navigation' | 'footer' | null}
 */
export function sharedSectionKey(category) {
  return isSharedCategory(category) ? category : null;
}

/**
 * Dotted store path for a shared section, or null.
 * @param {*} category
 * @returns {string | null}
 */
export function sharedSectionStorePath(category) {
  const key = sharedSectionKey(category);
  return key ? `sharedSections.${key}` : null;
}

/**
 * Display label for property-panel UI.
 * @param {*} category
 * @returns {string | null}
 */
export function sharedSectionLabel(category) {
  if (category === 'navigation') return 'Navigation';
  if (category === 'footer') return 'Footer';
  return null;
}
